import { Col } from '@amzn/stencil-react-components/layout';
import React, { useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router';
import { KatalLoggerClient } from 'src/logger/KatalLoggerClient';
import { FailureErrorView } from 'src/pages/FailureErrorView';
import {
  consumeLastFailureContext,
  FailureContext
} from 'src/utils/ErrorRedirectUtils';
import { MetricUtils } from 'src/utils/MetricUtils';

export const FailureErrorPage = () => {
  const { errorCode } = useParams() as { errorCode: string };
  // location.key is unique per navigation — even two navigations to the same
  // /error/:errorCode get distinct keys. Keying on it (not errorCode) means a
  // second failure that resolves to the SAME route code still re-consumes fresh
  // context and re-emits the metric, while remaining stable across StrictMode's
  // double render (same render pass -> same key).
  const location = useLocation();
  const navKey = location.key;

  // consumeLastFailureContext() reads AND clears module state, so it must run
  // exactly once per failure navigation.
  const failureContextRef = useRef<FailureContext | undefined>();
  const consumedKeyRef = useRef<string | undefined>();
  if (consumedKeyRef.current !== navKey) {
    consumedKeyRef.current = navKey;
    failureContextRef.current = consumeLastFailureContext();
  }
  const failureContext = failureContextRef.current;

  // Emit the page-load metric once per failure view, bucketed by class so error
  // volume stays trackable. Keyed on navKey so it re-fires for each distinct
  // failure navigation (React Router reuses this instance across them).
  useEffect(() => {
    const status = failureContext?.exception?.errorStatus?.toString();
    const metricBucket = status && /^4\d{2}$/.test(status) ? '4xx' : '5xx';
    MetricUtils.pageLoadCountMetrics(metricBucket, new KatalLoggerClient());
  }, [navKey]);

  return (
    <Col alignItems={'center'} padding={'3rem 1rem'}>
      <FailureErrorView errorCode={errorCode} failureContext={failureContext} />
    </Col>
  );
};
